import { applyPromoOverride } from 'yoda-core-components/lib/helpers/Stylitics';
import get from 'lodash/get';
import { miniPDPOnShopTheSetOpen } from 'yoda-site-components/lib/actions/MiniPDPAction';
import { shopTheSetLoadSuccess, shopTheSetClickEvent } from '../../../actions/AnalyticsAction';

let _onOutfitsLoaded = null;
let _onItemClick = null;
let _lastClickPosition = 0;
let _promoIds = new Set();
let _mobileObserver = null;

/**
 * Determines if the widget rendered in mobile layout.
 */
const isMobileLayout = () =>
    !!document.querySelector('#shopSetContainer .stylitics-ms-set-item[data-stylitics-item-id]');

/**
 * Applies or removes 'with code' text on the details panel sale price.
 */
const applyDetailsPromo = (saleEl, activeId) => {
    if (!saleEl) return;
    const existingPromo = saleEl.querySelector('.promotional-text');
    if (_promoIds.has(activeId)) {
        if (!existingPromo) {
            const span = document.createElement('span');
            span.className = 'promotional-text';
            span.textContent = ' with code';
            saleEl.classList.add('promotional-sale-price');
            saleEl.append(span);
        }
    } else if (existingPromo) {
        existingPromo.remove();
        saleEl.classList.remove('promotional-sale-price');
    }
};

/**
 * Applies promo for desktop layout carousels.
 */
const applyDesktop = () => {
    const container = document.getElementById('shopSetContainer');
    if (!container) return;
    container.querySelectorAll('.stylitics-ms-set-item-carousel').forEach((carousel) => {
        const activeId = carousel
            .querySelector('li[data-stylitics-id] .stylitics-ms-is-active-item')
            ?.closest('li[data-stylitics-id]')
            ?.getAttribute('data-stylitics-id');
        if (!activeId) return;
        const saleEl = carousel.querySelector(
            '.stylitics-ms-set-item-details .stylitics-ui-sale-price'
        );
        applyDetailsPromo(saleEl, activeId);
    });
};

/**
 * Applies promo for mobile layout sections.
 */
const applyMobile = () => {
    const container = document.getElementById('shopSetContainer');
    if (!container) return;
    container
        .querySelectorAll('.stylitics-ms-set-item[data-stylitics-item-id]')
        .forEach((section) => {
            const activeId = section.getAttribute('data-stylitics-item-id');
            if (!activeId) return;
            const saleEl = section.querySelector(
                '.stylitics-ms-set-item-details .stylitics-ui-sale-price'
            );
            applyDetailsPromo(saleEl, activeId);
        });
};

/**
 * Applies promo for all visible sections (handles both layouts).
 */
const applyAllSections = () => {
    if (!_promoIds.size) return;
    if (isMobileLayout()) {
        applyMobile();
    } else {
        applyDesktop();
    }
};

/**
 * Sets up mobile observer to detect item changes via attribute mutation.
 */
const setupMobileObserver = () => {
    if (_mobileObserver) return;
    const container = document.getElementById('shopSetContainer');
    if (!container) return;
    const sections = container.querySelectorAll('.stylitics-ms-set-item[data-stylitics-item-id]');
    if (!sections.length) return;

    _mobileObserver = new MutationObserver((mutations) => {
        mutations.forEach((m) => {
            if (m.attributeName === 'data-stylitics-item-id') {
                const section = m.target;
                const activeId = section.getAttribute('data-stylitics-item-id');
                if (!activeId) return;
                setTimeout(() => {
                    const saleEl = section.querySelector(
                        '.stylitics-ms-set-item-details .stylitics-ui-sale-price'
                    );
                    applyDetailsPromo(saleEl, activeId);
                }, 100);
            }
        });
    });
    sections.forEach((section) => {
        _mobileObserver.observe(section, {
            attributes: true,
            attributeFilter: ['data-stylitics-item-id'],
        });
    });
};

const shopTheSetConfig = {
    id: 'shop-the-set',
    containerId: 'shopSetContainer',
    title: 'Shop The Set',
    titleId: 'widget-title-STS',
    featureFlagKey: 'enableShopTheSet',
    imageQuery: '.stylitics-ui-image',

    getScriptURL: (prefs) => get(prefs, 'styliticsWidget.scriptURL', ''),

    createWidget: async (itemNumber) => {
        if (typeof StyliticsComposer === 'undefined') {
            throw new Error('StyliticsComposer is not available');
        }
        return StyliticsComposer.initialize({
            client: 'jcpenney_intimates_matching_sets',
            target: 'shopSetContainer',
            config: {
                modules: [
                    {
                        type: 'shop-the-set',
                        params: {
                            api: { min: 3, max: 10, item_number: itemNumber },
                            text: {
                                viewDetailsCTA: 'Shop The Set',
                                itemLinkCTA: 'Shop',
                            },
                        },
                        on: {
                            'mount.set': ({ items }) => {
                                _onOutfitsLoaded?.(items);
                                // Build promo lookup from items in the set
                                (Array.isArray(items) ? items : [])
                                    .filter((item) => item?.promotions?.includes('with_code'))
                                    .forEach((item) => _promoIds.add(String(item.item_id)));
                                if (!_promoIds.size) return;
                                // Apply promo on initial render
                                setTimeout(() => {
                                    applyAllSections();
                                    if (isMobileLayout()) setupMobileObserver();
                                }, 500);
                            },
                            'mount.item': ({ item, element }) => {
                                applyPromoOverride(element, item);
                                // Also track promo IDs for click-based apply
                                if (item?.item_id && item?.promotions?.includes('with_code')) {
                                    _promoIds.add(String(item.item_id));
                                }
                            },
                        },
                        override: {
                            'click.item': (data) => {
                                const item = data?.item;
                                const outfit = data?.outfit || {};
                                const position = (outfit?.items || []).findIndex(
                                    (i) => i.item_id === item?.item_id
                                );
                                _lastClickPosition = position >= 0 ? position : 0;
                                _onItemClick?.(item, outfit);
                                // After widget updates details panel, apply/remove promo
                                if (_promoIds.size) {
                                    setTimeout(() => applyAllSections(), 300);
                                }
                            },
                        },
                    },
                ],
            },
        });
    },

    registerEvents: (instance, { onOutfitsLoaded, onItemClick }) => {
        _onOutfitsLoaded = onOutfitsLoaded;
        _onItemClick = onItemClick;
    },

    refresh: (instance, itemNumber) => {
        if (_mobileObserver) {
            _mobileObserver.disconnect();
            _mobileObserver = null;
        }
        _promoIds = new Set();
        instance?.refresh?.({ sharedParams: { api: { item_number: itemNumber } } });
    },

    analyticsLoadAction: shopTheSetLoadSuccess,
    analyticsClickAction: ({ id, ppId }) =>
        shopTheSetClickEvent({
            shopTheSetID: id?.toString() || '',
            shopTheSetPPID: ppId,
            moduleName: 'shop the set',
            linkText: 'Shop',
            linkPosition: _lastClickPosition,
        }),
    miniPDPAction: miniPDPOnShopTheSetOpen,
    analyticsModule: 'shop the set',
};

export default shopTheSetConfig;
